import apiClient, { ApiResponse } from './client';
import { tokenStorage } from './client';
import { DIRECT_SALE_SESSION_HEADER, ensureSession } from './directSale';
import { logger } from '@/utils/logger';
import { MAX_UPLOAD_RETRIES, ASSET_URL_BATCH_SIZE } from '@/config/constants';
import { isApiErrorCode, type ApiErrorCode } from '@designdeck/shared';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface AssetUploadResponse {
  id: string;
  name: string;
  storageKey: string;
  url: string;
  mimeType: string;
  fileSize: number;
  width: number | null;
  height: number | null;
  designId?: string;
  createdAt: string;
}

export interface AssetUrlsResponse {
  urls: Record<string, string | null>;
}

export interface AssetDeleteResponse {
  id: string;
  deleted: boolean;
}

/**
 * A failed upload request. Carries the HTTP status and, when the API sent
 * one, its stable error code, so callers can translate the failure instead
 * of showing the raw server message.
 */
export class AssetUploadError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: ApiErrorCode
  ) {
    super(message);
    this.name = 'AssetUploadError';
  }
}

export interface UploadOptions {
  designId?: string;
  onProgress?: (percent: number) => void;
  maxRetries?: number;
}

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

/**
 * Sleep helper for retry logic
 */
const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Get API base URL
 */
const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;

  if (!envUrl) {
    return 'http://localhost:4000/api/v1';
  }

  if (!envUrl.endsWith('/api/v1')) {
    return `${envUrl.replace(/\/$/, '')}/api/v1`;
  }

  return envUrl;
};

// ═══════════════════════════════════════════════════════════════════
// UPLOAD WITH XHR (for progress tracking)
// ═══════════════════════════════════════════════════════════════════

/**
 * Perform the actual upload with XHR for progress tracking
 */
async function performUpload(
  file: File,
  designId?: string,
  onProgress?: (percent: number) => void
): Promise<AssetUploadResponse> {
  // Direct Sale customers are anonymous: the shared editor core calls this
  // same function, so the auth branch lives here rather than in 1500 lines
  // of editor code. The session token routes the upload to the shop-vouched
  // endpoint; a user JWT routes to the normal one.
  const isDirectSale =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/direct-sale');
  const sessionToken = isDirectSale ? (await ensureSession()).token : null;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const baseUrl = getApiBaseUrl();

    // Progress tracking
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          onProgress(percent);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          // Handle wrapped response { success: true, data: ... }
          const data = response.data || response;
          resolve(data);
        } catch {
          reject(new Error('Invalid JSON response'));
        }
      } else {
        let errorMessage = `Upload failed: ${xhr.status} ${xhr.statusText}`;
        let errorCode: ApiErrorCode | undefined;
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          if (errorResponse.message) {
            errorMessage = Array.isArray(errorResponse.message)
              ? errorResponse.message[0]
              : errorResponse.message;
          }
          if (isApiErrorCode(errorResponse.code)) {
            errorCode = errorResponse.code;
          }
        } catch {
          // Use default error message
        }
        reject(new AssetUploadError(errorMessage, xhr.status, errorCode));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.addEventListener('timeout', () => {
      reject(new Error('Upload timed out'));
    });

    // Prepare request
    xhr.open(
      'POST',
      sessionToken
        ? `${baseUrl}/assets/direct-sale/upload`
        : `${baseUrl}/assets/upload`
    );
    xhr.timeout = 60000; // 60 seconds for large files

    // Set auth header
    if (sessionToken) {
      xhr.setRequestHeader(DIRECT_SALE_SESSION_HEADER, sessionToken);
    } else {
      const token = tokenStorage.getAccessToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
    }

    // Build FormData
    const formData = new FormData();
    formData.append('file', file);
    // designId is a Seller-flow concept; the Direct Sale endpoint ignores it.
    if (designId && !sessionToken) {
      formData.append('designId', designId);
    }

    xhr.send(formData);
  });
}

// ═══════════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Upload an image file and create an asset
 *
 * Features:
 * - Retry logic with exponential backoff (default: 3 retries)
 * - Progress callback for large files
 * - Returns asset ID and URL
 *
 * @param file - Image file to upload
 * @param options - Upload options (designId, onProgress, maxRetries)
 * @returns Asset response with id, url, dimensions
 */
export async function uploadAsset(
  file: File,
  options?: UploadOptions
): Promise<AssetUploadResponse> {
  const {
    designId,
    onProgress,
    maxRetries = MAX_UPLOAD_RETRIES,
  } = options ?? {};
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.debug('AssetAPI', `Upload attempt ${attempt}/${maxRetries}`);
      return await performUpload(file, designId, onProgress);
    } catch (error) {
      lastError = error as Error;
      logger.warn(
        'AssetAPI',
        `Upload attempt ${attempt}/${maxRetries} failed:`,
        error
      );

      // The server rejected the file itself (unreadable PDF, wrong type,
      // too large): sending it again cannot help. Only a timeout or a
      // rate limit is worth another go.
      if (
        error instanceof AssetUploadError &&
        error.status >= 400 &&
        error.status < 500 &&
        error.status !== 408 &&
        error.status !== 429
      ) {
        throw error;
      }

      if (attempt < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = 1000 * Math.pow(2, attempt - 1);
        logger.debug('AssetAPI', `Retrying in ${delay}ms...`);
        await sleep(delay);
      }
    }
  }

  throw lastError ?? new Error('Upload failed after all retries');
}

/**
 * Resolve asset IDs to their current URLs
 *
 * Use this when loading a canvas to get current URLs for all assets.
 * Returns null for assets that don't exist or aren't accessible.
 *
 * @param assetIds - Array of asset IDs to resolve (max 50)
 * @returns Map of asset ID to URL (or null)
 */
export async function getAssetUrls(
  assetIds: string[]
): Promise<AssetUrlsResponse> {
  if (assetIds.length === 0) {
    return { urls: {} };
  }

  // Limit to batch size per request
  if (assetIds.length > ASSET_URL_BATCH_SIZE) {
    logger.warn('AssetAPI', `Truncating to ${ASSET_URL_BATCH_SIZE} asset IDs`);
    assetIds = assetIds.slice(0, ASSET_URL_BATCH_SIZE);
  }

  const response = await apiClient.post<ApiResponse<AssetUrlsResponse>>(
    '/assets/urls',
    { assetIds }
  );

  return response.data.data;
}

/**
 * Get details for a single asset
 *
 * @param assetId - Asset ID to fetch
 * @returns Asset details including URL
 */
export async function getAsset(assetId: string): Promise<AssetUploadResponse> {
  const response = await apiClient.get<ApiResponse<AssetUploadResponse>>(
    `/assets/${assetId}`
  );
  return response.data.data;
}

/**
 * Delete an asset
 *
 * @param assetId - Asset ID to delete
 * @returns Deletion confirmation
 */
export async function deleteAsset(
  assetId: string
): Promise<AssetDeleteResponse> {
  const response = await apiClient.delete<ApiResponse<AssetDeleteResponse>>(
    `/assets/${assetId}`
  );
  return response.data.data;
}

/**
 * Upload asset and return just the essential info for canvas
 * Convenience wrapper for quick integration
 *
 * @param file - Image file
 * @param onProgress - Optional progress callback
 * @returns Object with assetId and url
 */
export async function uploadAssetForCanvas(
  file: File,
  onProgress?: (percent: number) => void
): Promise<{
  assetId: string;
  url: string;
  width: number | null;
  height: number | null;
}> {
  const asset = await uploadAsset(file, { onProgress });
  return {
    assetId: asset.id,
    url: asset.url,
    width: asset.width,
    height: asset.height,
  };
}
